import { Dispatch, SetStateAction, useEffect, useState } from "react";

import { ProjectActivitiesConstants } from "../../../../../constants";
import { downloadGeneratedDocument } from "../../../../../route/developer/activities";
import { downloadActivityDocumentsZip } from "../../../../../service/activity";
import { searchActivityDefinitionDocumentTypes } from "../../../../../service/query/QueryService.full";
import { Status } from "../../../../../service/Shared";
import toast from "../../../../general/Toast/Toast";
import { ProfileMenuItemType } from "../../../../navigation";

interface useProgressIndicatorProps {
  activityHistoryUuid?: string;
  activityDefinitionUuid?: string;
  currentOnly: boolean;
  isVersionConflict: () => Promise<boolean>;
}

interface useProgressIndicatorReturnData {
  showSubmitActivityModal: boolean;
  setShowSubmitActivityModal: Dispatch<SetStateAction<boolean>>;
  showReturnToAssessorModal: boolean;
  setShowReturnToAssessorModal: Dispatch<SetStateAction<boolean>>;
  showCompleteActivityModal: boolean;
  setShowCompleteActivityModal: Dispatch<SetStateAction<boolean>>;
  showDeleteActivityModal: boolean;
  setShowDeleteActivityModal: Dispatch<SetStateAction<boolean>>;
  showDownloadDropdown: boolean;
  isDocumentDownloading: boolean;
  setShowDownloadDropdown: Dispatch<SetStateAction<boolean>>;
  dropdownOffsetX: number;
  setDropdownOffsetX: Dispatch<SetStateAction<number>>;
  dropdownOffsetY: number;
  setDropdownOffsetY: Dispatch<SetStateAction<number>>;
  downloadDropdownOptions: ProfileMenuItemType[];
  canOutputActivity: boolean;
  showDownloadActivityFilesModal: boolean;
  setShowDownloadActivityFilesModal: Dispatch<SetStateAction<boolean>>;
  setShowSubmitModalSwitcher: (activityStatus: string) => void;
  submitActivity: (activityStatus: string) => Promise<void>;
  completeActivity: () => Promise<void>;
}

export const useProgressIndicator = ({
  activityHistoryUuid,
  activityDefinitionUuid,
  currentOnly,
  isVersionConflict,
}: useProgressIndicatorProps): useProgressIndicatorReturnData => {
  const [canOutputActivity, setCanOutputActivity] = useState(false);
  const [showCompleteActivityModal, setShowCompleteActivityModal] = useState(false);
  const [showDeleteActivityModal, setShowDeleteActivityModal] = useState(false);
  const [showSubmitActivityModal, setShowSubmitActivityModal] = useState(false);
  const [showReturnToAssessorModal, setShowReturnToAssessorModal] = useState(false);
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);
  const [isDocumentDownloading, setIsDocumentDownloading] = useState(false);
  const [showDownloadActivityFilesModal, setShowDownloadActivityFilesModal] = useState(false);
  const [downloadDropdownOptions, setDownloadDropdownOptions] = useState<ProfileMenuItemType[]>([]);
  const [dropdownOffsetX, setDropdownOffsetX] = useState(0);
  const [dropdownOffsetY, setDropdownOffsetY] = useState(0);

  useEffect(() => {
    if (activityHistoryUuid) {
      setDownloadDropdownOptions([
        {
          id: 1,
          value: "Output",
          action: async () => {
            setIsDocumentDownloading(true);
            setShowDownloadDropdown(false);
            await downloadGeneratedDocument({ activityHistoryUuid, currentOnly });

            setIsDocumentDownloading(false);
          },
        },
        {
          id: 2,
          value: "Output + Key documents",
          action: async () => {
            setIsDocumentDownloading(true);
            setShowDownloadDropdown(false);
            const res = await downloadActivityDocumentsZip({ activityHistoryUuid, isKey: true });

            if (res.status === Status.Error) {
              toast.error({ message: "Could not download documents for this activity!" });

              return;
            }
            setIsDocumentDownloading(false);
            setShowDownloadActivityFilesModal(true);
          },
        },
        {
          id: 3,
          value: "Output + All documents",
          action: async () => {
            setIsDocumentDownloading(true);
            setShowDownloadDropdown(false);
            const res = await downloadActivityDocumentsZip({ activityHistoryUuid, isKey: false });

            if (res.status === Status.Error) {
              toast.error({ message: "Could not download documents for this activity!" });

              return;
            }
            setIsDocumentDownloading(false);
            setShowDownloadActivityFilesModal(true);
          },
        },
      ]);
    }
  }, [activityHistoryUuid, currentOnly]);

  useEffect(() => {
    let cancelled = false;

    if (activityDefinitionUuid) {
      setCanOutputActivity(false);

      searchActivityDefinitionDocumentTypes({
        paging: { limit: 1, beforeCursor: null, afterCursor: null },
        filter: {
          results: {
            activityDefinitionUuid: { operator: "eq", value: activityDefinitionUuid },
            activityDefinitionVersionDocumentTypes: {
              some: { templateFileUuid: { operator: "neq", value: null } },
            },
          },
        },
      }).then((response) => {
        if (cancelled) return;
        if (response.status === Status.Success && response.data) {
          setCanOutputActivity(response.data.results.length > 0);
        }
      });
    }

    return () => {
      cancelled = true;
    };
  }, [activityDefinitionUuid]);

  const setShowSubmitModalSwitcher = (activityStatus: string): void => {
    if (
      activityStatus === ProjectActivitiesConstants.STATUS_IN_PROGRESS ||
      activityStatus === ProjectActivitiesConstants.STATUS_REVIEW_CANCELLED
    ) {
      setShowSubmitActivityModal(true);
    } else if (activityStatus === ProjectActivitiesConstants.STATUS_CHANGES_REQUIRED) {
      setShowReturnToAssessorModal(true);
    }
  };

  const submitActivity = async (activityStatus: string): Promise<void> => {
    const isConflictModalOpen = await isVersionConflict();

    if (isConflictModalOpen) {
      return;
    }

    setShowSubmitModalSwitcher(activityStatus);
  };

  const completeActivity = async (): Promise<void> => {
    const isConflictModalOpen = await isVersionConflict();

    if (isConflictModalOpen) {
      return;
    }

    setShowCompleteActivityModal(true);
  };

  return {
    canOutputActivity,
    showReturnToAssessorModal,
    setShowReturnToAssessorModal,
    showSubmitActivityModal,
    setShowSubmitActivityModal,
    showCompleteActivityModal,
    setShowCompleteActivityModal,
    showDeleteActivityModal,
    setShowDeleteActivityModal,
    showDownloadDropdown,
    setShowDownloadDropdown,
    dropdownOffsetX,
    setDropdownOffsetX,
    dropdownOffsetY,
    setDropdownOffsetY,
    isDocumentDownloading,
    showDownloadActivityFilesModal,
    setShowDownloadActivityFilesModal,
    downloadDropdownOptions,
    setShowSubmitModalSwitcher,
    submitActivity,
    completeActivity,
  };
};
